'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {useRouter} from 'next/navigation';
import {type FC, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {trackProjectsListingRegisterInterestClick, trackProjectsListingViewItemClick} from '../../analytics';
import {AppIcon, FaLocationIcon} from '../icon';
import {Button, Typography} from '../primitives';
import {openRegisterInterest} from '../register-interest/register-interest-controller';
import {OrbitMedia} from './OrbitMedia';

export interface OrbitMediaBandItem {
  uuid: string;
  name: string;
  location?: string;
  description?: string;
  image: string;
  href: string;
}

export interface OrbitMediaBandProps {
  item: OrbitMediaBandItem;
}

const Band = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.colors.surface.neutral.inverseMuted,
  color: theme.palette.colors.text.neutral.inverse,
  overflow: 'hidden',
})) as FC<{children?: ReactNode}>;

const BandInner = styled(Box)(({theme}) => ({
  display: 'flex',
  // Mobile: media above the copy (Figma stacks the orbit image first); column-reverse
  // keeps the text in DOM order for AT while showing the image on top. Desktop: text left, media right.
  flexDirection: 'column-reverse',
  alignItems: 'center',
  gap: theme.spacing(6),
  paddingBlock: theme.spacing(6),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing(6),
    paddingBlock: 0,
  },
})) as FC<{children?: ReactNode}>;

const Copy = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 13,
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: 513,
  [theme.breakpoints.up('md')]: {gap: theme.spacing(2)},
})) as FC<{children?: ReactNode}>;

const Heading = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
}) as FC<{children?: ReactNode}>;

// stylis-plugin-rtl (cssjanus) already mirrors `to right` -> `to left` in RTL;
// a direction conditional here would double-flip and point the fade at the
// inline-start in Arabic.
const Divider = styled(Box)(({theme}) => ({
  display: 'none',
  height: 1,
  width: '100%',
  marginTop: theme.spacing(1),
  background: `linear-gradient(to right, ${theme.palette.colors.border.neutral.inverse}, transparent)`,
  [theme.breakpoints.up('md')]: {display: 'block'},
})) as FC;

const LocationRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  marginTop: 2,
  color: theme.palette.colors.text.primary.inverse,
  '& svg': {color: theme.palette.colors.icon.primary.inverse},
  [theme.breakpoints.up('md')]: {marginTop: theme.spacing(2)},
})) as FC<{children?: ReactNode}>;

const Actions = styled(Box)(({theme}) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: 5,
  [theme.breakpoints.up('md')]: {marginTop: 2},
})) as FC<{children?: ReactNode}>;

const MOBILE_MEDIA_BOX = 261;
const MOBILE_ORBIT = 297.04;
const MOBILE_IMAGE = 227.15;

const MediaCol = styled(Box)(({theme}) => ({
  flexShrink: 0,
  width: '100%',
  maxWidth: MOBILE_MEDIA_BOX,
  [theme.breakpoints.up('md')]: {width: 583, maxWidth: 583},
})) as FC<{children?: ReactNode}>;

// Percentage margins resolve against MediaCol's width, so MediaCol must stay padding-free.
const mobileMediaBleed: SxProps<Theme> = theme => ({
  [theme.breakpoints.down('md')]: {
    width: `${(MOBILE_ORBIT / MOBILE_MEDIA_BOX) * 100}%`,
    maxWidth: 'none',
    marginInlineStart: `${(1 - MOBILE_ORBIT / MOBILE_MEDIA_BOX) * 100}%`,
    marginInlineEnd: 0,
    marginBlock: `${((MOBILE_IMAGE - MOBILE_ORBIT) / 2 / MOBILE_MEDIA_BOX) * 100}%`,
  },
});

export function OrbitMediaBand({item}: OrbitMediaBandProps) {
  const {t} = useTranslation();
  const router = useRouter();
  return (
    <Band>
      <Container>
        <BandInner>
          <Copy>
            <Heading>
              <Typography
                size="body-3"
                weight="semiBold"
                family="primary"
                lineHeight="20"
                mobileSize="label"
                mobileWeight="regular"
                mobileLineHeight="20"
                as="p"
                sx={{
                  letterSpacing: {xs: '2.6px', md: '0.20em'},
                  color: theme => theme.palette.colors.text.primary.inverse,
                  textTransform: 'uppercase',
                }}
              >
                {t('featured_label')}
              </Typography>
              <Typography
                size="title-1"
                weight="bold"
                mobileSize="title-2"
                mobileWeight="bold"
                as="h2"
                sx={{textTransform: 'capitalize', color: theme => theme.palette.colors.text.neutral.inverse}}
              >
                {item.name}
              </Typography>
              <Divider />
              {item.location && (
                <LocationRow>
                  <AppIcon icon={FaLocationIcon} sx={{fontSize: '0.875em'}} aria-hidden />
                  <Typography size="body-2" weight="regular" as="span" sx={{color: theme => theme.palette.colors.text.primary.inverse}}>
                    {item.location}
                  </Typography>
                </LocationRow>
              )}
            </Heading>
            {item.description && (
              <Typography
                size="body-2"
                weight="regular"
                lineHeight="24"
                as="p"
                sx={{color: theme => theme.palette.colors.text.neutral.inverse}}
              >
                {item.description}
              </Typography>
            )}
            <Actions>
              <Button
                variant="contained-dark"
                onClick={() => {
                  trackProjectsListingRegisterInterestClick({id: item.uuid, name: item.name});
                  openRegisterInterest({projectId: item.uuid});
                }}
              >
                {t('register_interest')}
              </Button>
              <Button
                variant="outlined-dark"
                onClick={() => {
                  trackProjectsListingViewItemClick({id: item.uuid, name: item.name});
                  router.push(item.href);
                }}
              >
                {t('view_project')}
              </Button>
            </Actions>
          </Copy>
          <MediaCol>
            <OrbitMedia image={item.image} alt={item.name} sx={mobileMediaBleed} />
          </MediaCol>
        </BandInner>
      </Container>
    </Band>
  );
}

OrbitMediaBand.displayName = 'OrbitMediaBand';
